// src/RawItemsForm.js ver 01 Dec 03 2025 3:47 pm - SB115
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './App';
import * as rawItemsService from './api/rawItemsService';
import * as rawItemCategoryService from './api/rawItemCategoryService'; // To fetch categories
import { ApiError } from './api/apiClient';

const initialRawItemState = {
    RawItemName: '',
    AssociatedRawItemCategory: '',
    AssociatedRawItemCategorySeqId: '',
    Status: 'Active', // Default status
    // WithVariant: false, // Default boolean
};

// Allowed Status values (for frontend dropdown)
const rawItemStatuses = ['Active', 'Not-Active'];

function RawItemsForm() {
    // const { tenantId, id: rawItemId } = useParams();
    const { id: rawItemId } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(rawItemId);
    const auth = useAuth();

    const [formData, setFormData] = useState(initialRawItemState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rawItemNameError, setRawItemNameError] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]); // For dropdown
    const [categoryLookupError, setCategoryLookupError] = useState(null);

    const tenantId = auth.userProfile?.SubsCoDocId; // re-added ver01. existing in ver 00a
    console.log(`tenantId = ${tenantId}; rawItemId = ${rawItemId}`)


    useEffect(() => {
        const fetchData = async () => {
            if (!tenantId) {
                setError("Tenant ID missing in URL.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            setCategoryLookupError(null);

            try {
                const token = await auth.getIdToken();
                if (!token) {
                    setError("Authentication required. Please log in.");
                    setLoading(false);
                    return;
                }

                // Fetch categories for the dropdown
                const categoriesResponse = await rawItemCategoryService.getRawItemCategories(tenantId, token, 1, 1000, 'Active'); // Fetch active categories
                if (categoriesResponse && categoriesResponse.categories) {
                    setCategoryOptions(categoriesResponse.categories.map(cat => ({
                        name: cat.RawItemCategoryName,
                        seqId: cat.SeqId,
                    })));
                } else {
                    setCategoryLookupError("Failed to load raw item categories for dropdown.");
                }

                if (isEditing) {
                    const data = await rawItemsService.getRawItemById(tenantId, rawItemId, token);
                    if (data) {
                        setFormData(data);
                    } else {
                        setError("Raw Item not found.");
                        navigate(`/raw-items/tenant/${tenantId}`);
                    }
                } else {
                    // For new items, ensure a default category is pre-selected if options exist
                    if (categoriesResponse && categoriesResponse.categories.length > 0) {
                        const defaultCategory = categoriesResponse.categories[0];
                        setFormData(prev => ({
                            ...prev,
                            AssociatedRawItemCategory: defaultCategory.RawItemCategoryName,
                            AssociatedRawItemCategorySeqId: defaultCategory.SeqId,
                        }));
                    }
                }
            } catch (err) {
                console.error("Error fetching data for raw item form:", err);
                setError(`Failed to load data: ${err.message}`);
                setCategoryLookupError(`Failed to load categories: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [tenantId, rawItemId, isEditing, navigate, auth]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'AssociatedRawItemCategory') {
            // Find the selected category to get its SeqId
            const selectedCategory = categoryOptions.find(opt => opt.name === value);
            setFormData(prev => ({
                ...prev,
                [name]: value,
                AssociatedRawItemCategorySeqId: selectedCategory ? selectedCategory.seqId : '',
            }));
        } 
        // else if (name === 'WithVariant') {
        //     setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        // }
        else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setRawItemNameError(null);
        setCategoryLookupError(null);

        if (!tenantId) {
            setError("Tenant ID missing. Cannot save raw item.");
            return;
        }
        if (!formData.RawItemName || formData.RawItemName.trim() === '') {
            setRawItemNameError("Raw Item Name is required.");
            return;
        }
        if (!formData.AssociatedRawItemCategory || formData.AssociatedRawItemCategory.trim() === '' ||
            !formData.AssociatedRawItemCategorySeqId || formData.AssociatedRawItemCategorySeqId.trim() === '') {
            setCategoryLookupError("Associated Raw Item Category and its ID are required.");
            return;
        }
        
        try {
            const token = await auth.getIdToken();
            if (!token) {
                setError("Authentication required. Please log in.");
                return;
            }

            const payload = { ...formData };

            if (isEditing) {
                await rawItemsService.updateRawItem(tenantId, rawItemId, payload, token);
                alert("Raw Item updated successfully!");
            } else {
                await rawItemsService.addRawItem(tenantId, payload, token);
                alert("Raw Item created successfully!");
            }
            // navigate(`/raw-items/tenant/${tenantId}`);
            navigate(`/raw-items`);
        } catch (err) {
            console.error("Error saving raw item via Cloud Function:", err);
            if (err instanceof ApiError && err.status === 409) {
                setRawItemNameError(`Error: ${err.message}`);
                setError(null);
            } else {
                setError(`Failed to save raw item: ${err.message}`);
            }
        }
    };

    if (loading) return <p>Loading raw item details...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!tenantId) return <p className="container" style={{ color: 'red' }}>Error: Tenant ID is required to add/edit raw item.</p>;


    return (
        <div className="container form-container">
            <h2>{isEditing ? `Edit Raw Item (Tenant: ${tenantId})` : `Add New Raw Item (Tenant: ${tenantId})`}</h2>
            <form onSubmit={handleSubmit}>
                {isEditing && formData.SeqId && (
                    <div className="form-group">
                        <label>Sequential ID</label>
                        <input type="text" name="SeqId" value={formData.SeqId || ''} disabled />
                    </div>
                )}
                <div className="form-group">
                    <label>Raw Item Name</label>
                    <input
                        type="text"
                        name="RawItemName"
                        value={formData.RawItemName}
                        onChange={handleChange}
                        required
                    />
                    {rawItemNameError && <p style={{ color: 'red', fontSize: '0.85em' }}>{rawItemNameError}</p>}
                </div>

                <div className="form-group">
                    <label>RawItmCategory</label> {/* Label for dropdown */}
                    <select
                        name="AssociatedRawItemCategory"
                        value={formData.AssociatedRawItemCategory}
                        onChange={handleChange}
                        required
                        disabled={categoryOptions.length === 0 && !categoryLookupError && loading} // Disable if no options
                    >
                        <option value="">-- Select a Category --</option>
                        {categoryOptions.map(cat => (
                            <option key={cat.seqId} value={cat.name}>{cat.name}</option>
                        ))}
                    </select>
                    {categoryLookupError && <p style={{ color: 'red', fontSize: '0.85em' }}>{categoryLookupError}</p>}
                </div>

                <div className="form-group">
                    <label>RawItmCategId</label> {/* Display associated SeqId */}
                    <input
                        type="text"
                        name="AssociatedRawItemCategorySeqId"
                        value={formData.AssociatedRawItemCategorySeqId}
                        disabled // System-generated based on category selection
                    />
                </div>

                <div className="form-group">
                    <label>Status</label>
                    <select name="Status" value={formData.Status} onChange={handleChange} required>
                        {rawItemStatuses.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                </div>

                {/* <div className="form-group">
                    <label>With Variant</label>
                    <input
                        type="checkbox"
                        name="WithVariant"
                        checked={formData.WithVariant}
                        onChange={handleChange}
                        style={{ marginLeft: '10px', width: 'auto' }} // Adjust checkbox style
                    />
                </div> */}

                {isEditing && formData.DateAdded && (
                    <div className="form-group">
                        <label>Date Added</label>
                        <input type="text" value={new Date(formData.DateAdded._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}
                {isEditing && formData.DateLastModified && (
                    <div className="form-group">
                        <label>Date Last Modified</label>
                        <input type="text" value={new Date(formData.DateLastModified._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}

                <button type="submit">Save Raw Item</button>
            </form>
        </div>
    );
}

export default RawItemsForm;